/**
 * src/shell/agents/components/RetroDashboard.tsx
 *
 * Visual Data Storytelling Dashboard for Weekly Ops Retros:
 * - Velocity Trend & Test Health Metrics
 * - Milestone Timeline Cards
 * - Dual-Tab: Engineering Retro vs Investor Update
 * - Multi-Format Export (Markdown, PDF, Slack Blocks)
 */

import React, { useState } from 'react';
import { WeeklyRetroResult } from '../types/gstack-agents-types';
import { agentExportPipeline } from '../services/AgentExportPipeline';

interface RetroDashboardProps {
  result: WeeklyRetroResult;
}

export const RetroDashboard: React.FC<RetroDashboardProps> = ({ result }) => {
  const [viewTab, setViewTab] = useState<'team' | 'investor'>('team');
  const [exportFeedback, setExportFeedback] = useState<string | null>(null);

  const { report } = result;

  const handleExport = (format: 'markdown' | 'pdf' | 'slack_blocks') => {
    const content = viewTab === 'team' ? report.teamRetroDraft : report.investorUpdateDraft;
    const { mimeType, payload, downloadFilename } = agentExportPipeline.exportContent(
      content,
      format,
      `weekly-retro-${viewTab}`
    );
    agentExportPipeline.triggerClientDownload(payload, mimeType, downloadFilename);
    setExportFeedback(`Exported ${downloadFilename}`);
    setTimeout(() => setExportFeedback(null), 3000);
  };

  return (
    <div
      data-testid="retro-dashboard-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        background: '#0b0f19',
        borderRadius: 12,
        padding: 24,
        border: '1px solid rgba(255, 255, 255, 0.08)',
        color: '#f8fafc',
      }}
    >
      {/* Top Metrics Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
        }}
      >
        <div
          data-testid="metric-card-velocity"
          style={{
            background: 'rgba(30, 41, 59, 0.7)',
            padding: 16,
            borderRadius: 8,
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Velocity Points
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
            <span style={{ fontSize: 28, fontWeight: 800, color: '#38bdf8' }}>
              {report.metrics.velocityPoints}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#4ade80' }}>
              ↑{report.metrics.velocityTrendPct}% vs last wk
            </span>
          </div>
        </div>

        <div
          data-testid="metric-card-test-health"
          style={{
            background: 'rgba(30, 41, 59, 0.7)',
            padding: 16,
            borderRadius: 8,
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            CI / Test Health
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
            <span style={{ fontSize: 28, fontWeight: 800, color: '#4ade80' }}>
              {Math.round(report.metrics.testHealth.passRate * 100)}%
            </span>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              {report.metrics.testHealth.totalTests} tests (0 failures)
            </span>
          </div>
        </div>

        <div
          data-testid="metric-card-shipped"
          style={{
            background: 'rgba(30, 41, 59, 0.7)',
            padding: 16,
            borderRadius: 8,
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Milestones Shipped
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
            <span style={{ fontSize: 28, fontWeight: 800, color: '#a855f7' }}>
              {report.shipped.items.length}
            </span>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              {report.metrics.prsMergedCount} PRs merged
            </span>
          </div>
        </div>
      </div>

      {/* Shipped Deliverables Timeline */}
      <div style={{ background: 'rgba(15, 23, 42, 0.6)', padding: 18, borderRadius: 8 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#f1f5f9' }}>
          🚢 Shipped Deliverables
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {report.shipped.items.map((item) => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 14px',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: 6,
                border: '1px solid rgba(255, 255, 255, 0.05)',
              }}
            >
              <div>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    marginRight: 8,
                    background: item.type === 'feature' ? '#1e3a8a' : '#064e3b',
                    color: item.type === 'feature' ? '#93c5fd' : '#6ee7b7',
                  }}
                >
                  {item.type}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{item.title}</span>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{item.description}</div>
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{item.links?.join(' • ')}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Dual Tab: Team Retro vs Investor Update */}
      <div
        style={{
          background: 'rgba(15, 23, 42, 0.8)',
          borderRadius: 8,
          border: '1px solid rgba(255, 255, 255, 0.08)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 18px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            background: 'rgba(30, 41, 59, 0.5)',
          }}
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              data-testid="tab-retro-team"
              onClick={() => setViewTab('team')}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                background: viewTab === 'team' ? '#3b82f6' : 'transparent',
                color: viewTab === 'team' ? '#fff' : '#94a3b8',
                border: 'none',
              }}
            >
              👥 Team Retro
            </button>
            <button
              type="button"
              data-testid="tab-retro-investor"
              onClick={() => setViewTab('investor')}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                background: viewTab === 'investor' ? '#3b82f6' : 'transparent',
                color: viewTab === 'investor' ? '#fff' : '#94a3b8',
                border: 'none',
              }}
            >
              📈 Investor & Stakeholder Update
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              data-testid="btn-export-markdown"
              onClick={() => handleExport('markdown')}
              style={{
                padding: '4px 10px',
                fontSize: 11,
                borderRadius: 4,
                background: 'rgba(255,255,255,0.08)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.15)',
                cursor: 'pointer',
              }}
            >
              📄 Markdown
            </button>
            <button
              type="button"
              data-testid="btn-export-slack"
              onClick={() => handleExport('slack_blocks')}
              style={{
                padding: '4px 10px',
                fontSize: 11,
                borderRadius: 4,
                background: 'rgba(255,255,255,0.08)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.15)',
                cursor: 'pointer',
              }}
            >
              💬 Slack Blocks
            </button>
          </div>
        </div>

        {exportFeedback && (
          <div style={{ padding: '6px 18px', background: '#065f46', color: '#6ee7b7', fontSize: 12 }}>
            ✓ {exportFeedback}
          </div>
        )}

        <div style={{ padding: 18 }}>
          <pre
            data-testid="retro-content-preview"
            style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: 12,
              lineHeight: 1.6,
              color: '#cbd5e1',
              margin: 0,
            }}
          >
            {viewTab === 'team' ? report.teamRetroDraft : report.investorUpdateDraft}
          </pre>
        </div>
      </div>
    </div>
  );
};
